type GuideTo =
	| "/app/help/user-guide"
	| "/app/help/user-guide/establishments"
	| "/app/help/user-guide/livestock"
	| "/app/help/user-guide/grass-production"
	| "/app/help/user-guide/financial"
	| "/app/help/user-guide/equipment"
	| "/app/help/user-guide/planning"
	| "/app/help/user-guide/assistant";

interface HelpHomeTopic {
	id: string;
	title: string;
	category: string;
	description: string;
	steps: [string, string, string];
	tip: string;
	keywords: string;
	guideTo: GuideTo;
}

interface HelpHomeContent {
	title: string;
	description: string;
	searchLabel: string;
	searchPlaceholder: string;
	clearSearch: string;
	results: string;
	resultOne: string;
	noResultsTitle: string;
	noResultsBody: string;
	browseAll: string;
	topicsTitle: string;
	topicsDescription: string;
	startTitle: string;
	startBody: string;
	startAction: string;
	supportTitle: string;
	supportBody: string;
	supportAction: string;
	ticketsAction: string;
	faqTitle: string;
	guideLink: string;
	topics: HelpHomeTopic[];
	startSteps: { title: string; body: string }[];
	faq: { id: string; question: string; answer: string }[];
}

export const helpHomeContent: Record<"pt_BR" | "en_US", HelpHomeContent> = {
	pt_BR: {
		title: "Ajuda para a rotina da fazenda",
		description:
			"Encontre o próximo passo para organizar o rebanho, registrar o trabalho e acompanhar seus resultados.",
		searchLabel: "Buscar nesta página de ajuda",
		searchPlaceholder: "Pesagem, piquete, conta…",
		clearSearch: "Limpar busca",
		results: "{{count}} resultados encontrados",
		resultOne: "1 resultado encontrado",
		noResultsTitle: "Não encontramos esse assunto por aqui.",
		noResultsBody:
			"Tente uma palavra como animal, pagamento ou offline. Você também pode consultar os guias ou falar com o suporte.",
		browseAll: "Ver todos os tópicos",
		topicsTitle: "O que você quer fazer?",
		topicsDescription:
			"Abra um assunto para ver por onde começar e o que conferir em cada etapa.",
		startTitle: "Do primeiro cadastro à rotina",
		startBody:
			"Você não precisa cadastrar tudo de uma vez. Esta sequência ajuda a conectar os registros, mas pode ser adaptada à sua rotina.",
		startAction: "Ver guia de fazendas",
		supportTitle: "Ainda precisa de uma mão?",
		supportBody:
			"Conte em qual tela está, o que tentou fazer e qual mensagem apareceu. Isso ajuda a equipe a entender seu caso.",
		supportAction: "Pedir ajuda ao suporte",
		ticketsAction: "Acompanhar meus chamados",
		faqTitle: "Dúvidas que aparecem no caminho",
		guideLink: "Consultar o guia do módulo",
		startSteps: [
			{
				title: "Conheça sua fazenda",
				body: "Em Configurações → Fazendas, cadastre sua fazenda. Confira a organização selecionada e combine os acessos da equipe com quem administra a conta.",
			},
			{
				title: "Organize piquetes e lotes",
				body: "Cadastre os piquetes e agrupe os animais em lotes. Para registrar uma rotação, o piquete de destino precisa estar cadastrado.",
			},
			{
				title: "Registre o trabalho de hoje",
				body: "Comece por um animal, uma pesagem, um manejo ou uma despesa. Cada registro ajuda a construir o histórico da sua operação.",
			},
		],
		topics: [
			{
				id: "farm",
				title: "Organizar a fazenda e os acessos",
				category: "Fazendas e equipe",
				description:
					"Entenda onde seus registros ficam e como trabalhar com outras pessoas.",
				steps: [
					"Confira a organização ativa e selecione a fazenda em que vai trabalhar.",
					"Consulte o cadastro da fazenda e complete as informações necessárias para sua operação.",
					"Quem administra a organização pode convidar a equipe e definir as permissões de cada pessoa.",
				],
				tip: "Organização reúne a equipe e suas fazendas. A seleção de fazenda define quais registros aparecem nas telas com esse filtro.",
				keywords:
					"organização propriedade estabelecimento convite membro usuário permissão acesso cadastro equipe",
				guideTo: "/app/help/user-guide/establishments",
			},
			{
				id: "animals",
				title: "Cadastrar ou importar os animais",
				category: "Rebanho",
				description: "Comece por um brinco ou traga uma lista que você já usa.",
				steps: [
					"Abra Rebanho e vá para Animais. Escolha adicionar um animal ou importar uma lista.",
					"No cadastro individual, informe brinco e sexo; acrescente raça, lote e outros dados que tiver.",
					"Na importação, confira as linhas e os lotes antes de confirmar. Corrija os dados e pule animais que já existem.",
				],
				tip: "O lote é opcional no cadastro individual. Você pode organizar os animais depois, sem esperar para começar.",
				keywords:
					"animal boi vaca bezerro gado pecuária rebanho brinco planilha excel pdf foto caderno importação raça sexo",
				guideTo: "/app/help/user-guide/livestock",
			},
			{
				id: "records",
				title: "Anotar pesagens e manejos",
				category: "Rebanho",
				description: "Guarde o que aconteceu com um animal ou com o lote.",
				steps: [
					"Abra o animal ou lote e use a ação de novo registro.",
					"Escolha pesagem, vacinação ou o manejo realizado. Confira o destino, a data e os campos da atividade.",
					"Salve e confira o histórico. Para pesar vários animais no tronco, use a sessão de pesagem e salve ao terminar.",
				],
				tip: "Os avisos de atenção ajudam a encontrar registros pendentes. Confira o histórico do animal antes de repetir um lançamento.",
				keywords:
					"peso pesagem vacina vacinação manejo tratamento medição sanidade saúde histórico registro lote",
				guideTo: "/app/help/user-guide/livestock",
			},
			{
				id: "pasture",
				title: "Cadastrar piquetes e rotacionar lotes",
				category: "Piquetes",
				description:
					"Conecte a área de pasto ao lote que está usando o piquete.",
				steps: [
					"Abra Piquetes e cadastre o piquete com nome e área. Você pode desenhar seu contorno no mapa.",
					"Em Rebanho, crie ou abra o lote que vai ocupar essa área.",
					"No detalhe do lote, escolha a ação de atribuir piquete ou rotacionar. Confira o piquete de destino e a data antes de salvar.",
				],
				tip: "O piquete precisa existir antes da rotação. Registrar a rotação mantém o histórico de uso da pastagem.",
				keywords:
					"pasto pastagem piquete área hectare mapa contorno rotação rotacionar lote forragem",
				guideTo: "/app/help/user-guide/grass-production",
			},
			{
				id: "money",
				title: "Lançar uma conta e registrar o pagamento",
				category: "Financeiro",
				description: "Separe o que você deve do que já foi pago.",
				steps: [
					"Abra Financeiro, vá para Contas a pagar e crie uma conta.",
					"Selecione o fornecedor ou cadastre um ali mesmo. Confira a emissão, o vencimento e o valor de cada parcela.",
					"Depois de salvar, abra a conta para registrar os pagamentos e acompanhar o saldo em aberto.",
				],
				tip: "Criar uma conta não significa pagá-la. Uma conta incorreta é anulada; os pagamentos são registrados separadamente para preservar o histórico.",
				keywords:
					"dinheiro financeiro conta boleto nota fornecedor parcela vencimento despesa custo pagamento pagar anular",
				guideTo: "/app/help/user-guide/financial",
			},
			{
				id: "equipment",
				title: "Organizar máquinas e manutenção",
				category: "Equipamentos",
				description:
					"Identifique a máquina, sua fazenda e o que precisa de atenção.",
				steps: [
					"Abra Equipamentos e adicione a máquina à frota.",
					"Informe identificação, nome e categoria. Confira a fazenda de localização e acrescente os dados que tiver.",
					"No detalhe da máquina, consulte as ações de inspeção, relato de quebra e edição do cadastro.",
				],
				tip: "Se uma máquina não aparecer, confira a fazenda selecionada e a localização no cadastro dela.",
				keywords:
					"equipamento máquina trator frota manutenção quebra inspeção peças localização",
				guideTo: "/app/help/user-guide/equipment",
			},
			{
				id: "tasks",
				title: "Planejar e acompanhar as tarefas",
				category: "Planejamento",
				description:
					"Transforme o trabalho a fazer em uma rotina que a equipe consegue acompanhar.",
				steps: [
					"Abra Planejamento e consulte as tarefas existentes antes de criar uma nova.",
					"Descreva o trabalho e preencha os campos de prazo e responsabilidade conforme sua rotina.",
					"Acompanhe o andamento e marque a tarefa como concluída quando o trabalho terminar.",
				],
				tip: "Use títulos concretos, como “Conferir a cerca do piquete 3”, para que a próxima pessoa saiba o que fazer.",
				keywords:
					"planejamento tarefa atividade prazo responsável calendário objetivo concluir equipe rotina",
				guideTo: "/app/help/user-guide/planning",
			},
			{
				id: "assistant",
				title: "Perguntar ou registrar com ajuda da IA",
				category: "Assistente de IA",
				description:
					"Converse sobre seus registros e confira as sugestões antes de aplicá-las.",
				steps: [
					"Abra o assistente e diga o que quer consultar ou registrar. Informe a fazenda e identifique o animal, lote ou equipamento.",
					"Para capturar o trabalho, você também pode usar os atalhos de voz, foto ou descrição disponíveis na página inicial.",
					"Quando houver uma proposta de alteração, confira cada item, corrija o necessário e aprove apenas se tiver permissão.",
				],
				tip: "Uma proposta ainda não é um registro aplicado. Conversar com o assistente e aprovar propostas são permissões diferentes.",
				keywords:
					"ia inteligência artificial assistente conversa voz áudio foto imagem descrição proposta sugestão aprovar",
				guideTo: "/app/help/user-guide/assistant",
			},
			{
				id: "offline",
				title: "Trabalhar no campo sem sinal",
				category: "Uso offline",
				description:
					"Prepare os dados antes de sair e confira os envios na volta.",
				steps: [
					"Ainda com internet, abra a Central Offline no menu lateral ou em Mais no celular. Prepare os dados de que vai precisar.",
					"Use as telas com suporte offline, como cadastro de animal, pesagem e manejo. O que for registrado entra na fila de envio.",
					"Quando a conexão voltar, acompanhe a sincronização e confira se cada item foi enviado ou precisa de atenção.",
				],
				tip: "Pendente de envio não significa salvo no servidor. Cadastro de equipamentos e contas completas, por exemplo, precisam de conexão.",
				keywords:
					"offline sem internet sem sinal conexão sincronização sincronizar pendente envio fila celular campo baixar dados",
				guideTo: "/app/help/user-guide",
			},
		],
		faq: [
			{
				id: "scope",
				question: "Por que um registro não aparece?",
				answer:
					"Confira a organização, a fazenda selecionada e os filtros da tela. Um animal vendido, por exemplo, pode ficar fora do filtro de animais ativos. Equipamentos também dependem da localização cadastrada.",
			},
			{
				id: "access",
				question: "Por que não vejo uma opção ou não consigo salvar?",
				answer:
					"As opções dependem das permissões da sua conta e, em algumas telas, da conexão. Confira o aviso exibido. Se for uma questão de acesso, peça a quem administra a organização para revisar suas permissões.",
			},
			{
				id: "approval",
				question: "A IA altera meus registros sozinha?",
				answer:
					"Não. Ao pedir um lançamento ou alteração, o assistente prepara uma proposta. Uma pessoa com permissão precisa conferir e aprovar para que ela seja aplicada. Você pode revisar os itens ou rejeitar a proposta.",
			},
			{
				id: "pending",
				question: "Registrei sem sinal. Já está tudo salvo?",
				answer:
					"O registro fica no dispositivo, na fila de envio, até ser sincronizado. Ao voltar à internet, confira a central offline e a confirmação de cada item. Se houver falha, veja o motivo antes de repetir o lançamento.",
			},
		],
	},
	en_US: {
		title: "Help with your farm’s daily work",
		description:
			"Find your next step to organize livestock, record the work and follow your results.",
		searchLabel: "Search this help page",
		searchPlaceholder: "Weighing, paddock, bill…",
		clearSearch: "Clear search",
		results: "{{count}} results found",
		resultOne: "1 result found",
		noResultsTitle: "We couldn't find that topic here.",
		noResultsBody:
			"Try a word like animal, payment or offline. You can also browse the guides or contact support.",
		browseAll: "View all topics",
		topicsTitle: "What would you like to do?",
		topicsDescription:
			"Open a topic to see where to start and what to check along the way.",
		startTitle: "From setup to daily work",
		startBody:
			"You don't need to enter everything at once. This suggested sequence helps connect your records, and you can adapt it to your routine.",
		startAction: "Read the farm guide",
		supportTitle: "Still need a hand?",
		supportBody:
			"Tell us which screen you're on, what you tried to do and any message that appeared. This helps the team understand your case.",
		supportAction: "Contact support",
		ticketsAction: "Follow my tickets",
		faqTitle: "Questions along the way",
		guideLink: "Read the module guide",
		startSteps: [
			{
				title: "Get to know your farm",
				body: "Add your farm in Settings → Farms. Check the selected organization and agree on team access with the person who administers your account.",
			},
			{
				title: "Organize paddocks and herds",
				body: "Add paddocks and group animals into herds. Before recording a rotation, make sure the destination paddock is already registered in your farm.",
			},
			{
				title: "Record today's work",
				body: "Start with an animal, a weighing, a management event or an expense. Each new record helps build the history of your operation.",
			},
		],
		topics: [
			{
				id: "farm",
				title: "Set up your farm and team access",
				category: "Farms and team",
				description:
					"Understand where records belong and how to work with other people.",
				steps: [
					"Check the active organization and select the farm you will work on.",
					"Open the farm's details and complete the information your operation needs.",
					"An organization administrator can invite team members and set each person's permissions.",
				],
				tip: "An organization brings your team and farms together. The farm selection determines which records appear on screens that use this filter.",
				keywords:
					"organization property establishment invitation member user permission access setup team farm",
				guideTo: "/app/help/user-guide/establishments",
			},
			{
				id: "animals",
				title: "Add or import your animals",
				category: "Livestock",
				description: "Start with one ear tag or bring a list you already use.",
				steps: [
					"Open Livestock and go to Animals. Choose to add an animal or import a list.",
					"For an individual animal, enter its ear tag and sex; add breed, herd and any other details you have.",
					"When importing, review the rows and herd assignments before confirming. Correct values and skip animals that already exist.",
				],
				tip: "A herd is optional when adding an individual animal. You can organize animals later and start recording now.",
				keywords:
					"animal cow cattle calf livestock ear tag spreadsheet excel pdf photo notebook import breed sex",
				guideTo: "/app/help/user-guide/livestock",
			},
			{
				id: "records",
				title: "Record weights and management events",
				category: "Livestock",
				description: "Keep a record of what happened to an animal or a herd.",
				steps: [
					"Open the animal or herd and choose the new record action.",
					"Choose weighing, vaccination or the activity performed. Check the target, date and activity fields.",
					"Save and check the history. To weigh several animals at the chute, use a weighing session and save it when finished.",
				],
				tip: "Attention notices help you find missing records. Check an animal's history before repeating an entry.",
				keywords:
					"weight weighing vaccine vaccination management treatment measurement health history record herd",
				guideTo: "/app/help/user-guide/livestock",
			},
			{
				id: "pasture",
				title: "Add paddocks and rotate herds",
				category: "Paddocks",
				description: "Connect a grazing area to the herd using it.",
				steps: [
					"Open Paddocks and add a paddock with its name and area. You can draw its boundary on the map.",
					"In Livestock, create or open the herd that will use this area.",
					"On the herd detail page, choose the assign paddock or rotate action. Check the destination paddock and date before saving.",
				],
				tip: "The paddock must exist before a rotation. Recording a rotation preserves the pasture's usage history.",
				keywords:
					"pasture grass production paddock area hectare map boundary rotation rotate herd forage",
				guideTo: "/app/help/user-guide/grass-production",
			},
			{
				id: "money",
				title: "Enter a bill and record its payment",
				category: "Financial",
				description: "Keep what you owe separate from what you've paid.",
				steps: [
					"Open Financial, go to Payables and create a bill.",
					"Select a vendor or create one right there. Check the issue date, due date and amount of each installment.",
					"Once saved, open the bill to record payments and follow the outstanding balance.",
				],
				tip: "Creating a bill does not pay it. An incorrect bill is voided; payments are recorded separately to preserve the history.",
				keywords:
					"money financial bill invoice vendor installment due expense cost payment payable void",
				guideTo: "/app/help/user-guide/financial",
			},
			{
				id: "equipment",
				title: "Organize machinery and maintenance",
				category: "Equipment",
				description: "Identify the machine, its farm and what needs attention.",
				steps: [
					"Open Equipment and add the machine to your fleet.",
					"Enter its asset ID, name and category. Check the farm location and add the details you have.",
					"On the machine detail page, find actions to start an inspection, report a breakdown or edit its details.",
				],
				tip: "If a machine is missing, check the selected farm and the location in the machine's details.",
				keywords:
					"equipment machine tractor fleet maintenance breakdown inspection parts location",
				guideTo: "/app/help/user-guide/equipment",
			},
			{
				id: "tasks",
				title: "Plan and follow your tasks",
				category: "Planning",
				description: "Turn work to be done into a routine the team can follow.",
				steps: [
					"Open Planning and check existing tasks before adding a new one.",
					"Describe the work and fill in the deadline and assignment fields to suit your routine.",
					"Follow its progress and mark the task complete when the work is finished.",
				],
				tip: "Use concrete titles, such as “Check the fence in paddock 3”, so the next person knows what to do.",
				keywords:
					"planning task activity deadline assignee calendar goal complete team routine",
				guideTo: "/app/help/user-guide/planning",
			},
			{
				id: "assistant",
				title: "Ask questions or record with AI help",
				category: "AI Assistant",
				description:
					"Discuss your records and check suggestions before applying them.",
				steps: [
					"Open the assistant and explain what you want to look up or record. Specify the farm and identify the animal, herd or equipment.",
					"You can also capture work using the voice, photo or description shortcuts available on the home page.",
					"When a change proposal appears, review each item, correct anything needed and approve only if you have permission.",
				],
				tip: "A proposal is not yet an applied record. Chatting with the assistant and approving proposals require different permissions.",
				keywords:
					"ai artificial intelligence assistant chat voice audio photo image description proposal suggestion approve",
				guideTo: "/app/help/user-guide/assistant",
			},
			{
				id: "offline",
				title: "Work in the field without a signal",
				category: "Offline use",
				description:
					"Prepare your data before leaving and check uploads when you return.",
				steps: [
					"While online, open Offline Center in the sidebar, or under More on mobile. Prepare the data sets you will need.",
					"Use supported offline screens, such as animal registration, weighing and management records. New records enter the upload queue.",
					"When the connection returns, follow synchronization and check whether each item was sent or needs attention.",
				],
				tip: "Pending upload does not mean saved on the server. Equipment registration and full supplier bills, for example, need a connection.",
				keywords:
					"offline no internet no signal connection synchronization sync pending upload queue mobile field download data",
				guideTo: "/app/help/user-guide",
			},
		],
		faq: [
			{
				id: "scope",
				question: "Why can't I see a record?",
				answer:
					"Check the organization, selected farm and screen filters. A sold animal, for example, may be excluded by the active animals filter. Equipment also depends on the location in its details.",
			},
			{
				id: "access",
				question: "Why is an option missing, or why can't I save?",
				answer:
					"Available options depend on your permissions and, on some screens, your connection. Check the message shown. For an access issue, ask your organization administrator to review your permissions.",
			},
			{
				id: "approval",
				question: "Does AI change my records on its own?",
				answer:
					"No. When you request an entry or change, the assistant prepares a proposal. Someone with permission must review and approve it before it is applied. You can review the items or reject the proposal.",
			},
			{
				id: "pending",
				question: "I recorded something offline. Is it saved?",
				answer:
					"The record stays on your device in the upload queue until it synchronizes. When you are online again, check the offline center and each item's confirmation. If something fails, check the reason before entering it again.",
			},
		],
	},
};
